import { cva } from "class-variance-authority";
import { Check, Minus } from "lucide-react";
import { type ChangeEvent, type MouseEvent, useId } from "react";
import { cn } from "../../utils";
import { Text } from "../Typography";
import type { CheckboxProps } from "./types";

const rootVariants = cva(
  "group box-border inline-flex cursor-pointer outline-none gap-3",
  {
    variants: {
      checkboxPosition: {
        start: "",
        end: "flex-row-reverse",
      },
      disabled: {
        true: "pointer-events-none",
        false: "",
      },
      isCard: {
        true: "peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-primary-500",
        false: "",
      },
      hasDescription: {
        true: "items-start",
        false: "items-center",
      },
    },
  },
);

const checkboxVariants = cva(
  "relative box-border rounded shrink-0 flex items-center justify-center border-2 motion-safe:transition-colors motion-safe:duration-200 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-primary-500 pointer-events-none",
  {
    variants: {
      size: {
        sm: "w-5 h-5 rounded",
        md: "w-6 h-6 rounded-[6px]",
      },
      disabled: {
        true: "bg-neutral-500/5 border-neutral-500/10",
        false: "",
      },
      checked: {
        true: "bg-primary-500 border-primary-500",
        false: "bg-transparent border-neutral-500/10",
      },
      isCard: {
        true: "peer-focus-visible:outline-none peer-focus-visible:outline-0",
        false: "",
      },
      hasDescription: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      {
        disabled: true,
        checked: false,
        class: "bg-neutral-500/10 border-neutral-500/10",
      },
      {
        isCard: false,
        disabled: false,
        checked: false,
        class: "group-hover:border-neutral-500/30",
      },
      {
        isCard: false,
        disabled: false,
        checked: true,
        class: "group-hover:bg-primary-400 group-hover:border-primary-400",
      },
      {
        size: "sm",
        hasDescription: true,
        class: "mt-[2px]",
      },
    ],
  },
);

export const Checkbox = ({
  checked,
  onChange,
  label,
  description,
  accessory,
  checkboxPosition = "end",
  disabled = false,
  className,
  id,
  style,
  isCard = false,
  indeterminate = false,
  size = "md",
  onClick,
  children,
  ...inputProps
}: CheckboxProps) => {
  const fallbackId = useId();
  const checkboxId = id || fallbackId;

  const iconSize = size === "sm" ? 14 : 16;

  const rootClass = cn(
    rootVariants({
      checkboxPosition,
      disabled,
      isCard,
      hasDescription: !!description,
    }),
    className,
  );

  const checkboxClass = cn(
    checkboxVariants({
      size,
      disabled,
      checked,
      isCard,
      hasDescription: !!description,
    }),
  );

  const checkIconClass = cn(
    disabled && checked ? "text-neutral-500/15" : "text-white",
    "motion-safe:transition-all motion-safe:duration-200",
    checked ? "opacity-100" : "opacity-0",
  );

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.checked, e);
  };

  const handleLabelClick = (e: MouseEvent<HTMLLabelElement>) => {
    if (onClick) {
      onClick(e as unknown as MouseEvent<HTMLInputElement>);
    }
  };

  const descriptionId = description ? `${checkboxId}__desc` : undefined;
  const accessoryId = accessory ? `${checkboxId}-accessory` : undefined;

  return (
    <label
      className={rootClass}
      htmlFor={checkboxId}
      data-iscard={isCard}
      style={style}
      onClick={handleLabelClick}
    >
      <div className="relative inline-flex">
        <input
          {...inputProps}
          id={checkboxId}
          type="checkbox"
          className={cn(
            "peer absolute inset-0 z-[1] w-full h-full opacity-0 appearance-none cursor-pointer",
            disabled && "pointer-events-none cursor-default",
          )}
          tabIndex={disabled ? -1 : 0}
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          aria-label={label ?? inputProps["aria-label"]}
          aria-describedby={
            [descriptionId, accessoryId].filter(Boolean).join(" ") || undefined
          }
        />

        <div className={checkboxClass} style={{ outlineOffset: 1 }}>
          {indeterminate ? (
            <Minus className={checkIconClass} size={iconSize} />
          ) : (
            <Check className={checkIconClass} size={iconSize} />
          )}
        </div>
      </div>

      {children ? (
        <div className="flex flex-col w-full">{children}</div>
      ) : (
        (label || description) && (
          <div className="flex flex-col w-full">
            {label && (
              <div className="flex items-center gap-2">
                <Text size={size} className="text-neutral-500">
                  {label}
                </Text>
                {accessory && (
                  <div id={accessoryId} role="group">
                    {accessory}
                  </div>
                )}
              </div>
            )}
            {description && (
              <Text
                as="div"
                size={size === "sm" ? "xs" : "sm"}
                className="text-neutral-500/50"
                id={descriptionId}
              >
                {description}
              </Text>
            )}
          </div>
        )
      )}
    </label>
  );
};
